import { CalendarDateFromString } from "./temporal.js";
import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { RiskLanguage, RiskTemplateAttachmentRow, RiskTemplateLawRow, RiskTemplateTagRow, } from "./admin-risk-templates.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
/**
 * `risk_instances` is client-scoped (unlike `risk_templates`): every read
 * and write below re-checks `Principal` against the row's own `client_id`
 * the same way `admin-locations` does, so an external SiFa never reaches a
 * client outside `assignedClientIds`.
 */
export const RiskInstanceStatus = S.Literal("draft", "instance", "assigned", "accepted", "rejected", "mitigated");
export const RiskInstanceMeasureTemplateSummary = S.Struct({
    id: S.UUID,
    title: S.String,
    description: S.NullOr(S.String),
    priority: S.String,
    category: S.String,
    recurring: S.Boolean,
    recurrence: S.NullOr(S.String),
    next_occurrence: S.NullOr(S.String),
    due_date: S.NullOr(CalendarDateFromString),
    due_date_interval: S.NullOr(S.String),
    auto_reminder: S.Boolean,
    auto_assign_new_employees: S.Boolean,
});
export const LocationRiskFieldSummary = S.Struct({
    id: S.UUID,
    custom_name: S.NullOr(S.String),
    value: S.NullOr(S.String),
    unique_label: S.NullOr(S.String),
});
export const RiskInstanceRow = S.Struct({
    id: S.UUID,
    language: RiskLanguage,
    translation_group_id: S.NullOr(S.Int),
    client_id: S.UUID,
    client_name: S.NullOr(S.String),
    client_company_name: S.NullOr(S.String),
    risk_template_id: S.NullOr(S.UUID),
    template_title: S.NullOr(S.String),
    template_description: S.NullOr(S.String),
    location_id: S.NullOr(S.UUID),
    location_name: S.NullOr(S.String),
    location_key: S.NullOr(S.String),
    is_company_wide: S.Boolean,
    title: S.String,
    description: S.NullOr(S.String),
    severity: S.Int,
    severity_label: S.NullOr(S.String),
    probability: S.Int,
    probability_label: S.NullOr(S.String),
    score: S.NullOr(S.Int),
    status: RiskInstanceStatus,
    area_translation_group_id: S.NullOr(S.Int),
    object_translation_group_id: S.NullOr(S.Int),
    activity_translation_group_id: S.NullOr(S.Int),
    factor_translation_group_id: S.NullOr(S.Int),
    subfactor_translation_group_id: S.NullOr(S.Int),
    source_translation_group_id: S.NullOr(S.Int),
    mode_translation_group_id: S.NullOr(S.Int),
    area: S.NullOr(S.String),
    object: S.NullOr(S.String),
    activity: S.NullOr(S.String),
    factor: S.NullOr(S.String),
    subfactor: S.NullOr(S.String),
    source: S.NullOr(S.String),
    mode: S.NullOr(S.String),
    location_areas: S.NullOr(S.Array(LocationRiskFieldSummary)),
    location_objects: S.NullOr(S.Array(LocationRiskFieldSummary)),
    location_activities: S.NullOr(S.Array(LocationRiskFieldSummary)),
    laws: S.Array(RiskTemplateLawRow),
    tags: S.Array(RiskTemplateTagRow),
    attachments: S.Array(RiskTemplateAttachmentRow),
    measure_templates: S.Array(RiskInstanceMeasureTemplateSummary),
    accepted_on: S.NullOr(S.String),
    accepted_by: S.NullOr(S.UUID),
    rejected_on: S.NullOr(S.String),
    rejected_by: S.NullOr(S.UUID),
    rejection_reason: S.NullOr(S.String),
    /**
     * The comment an inspection captured against this risk, newest first when a
     * risk was seen by several. Read-only here: it is written during an
     * inspection and this group has no way to change it.
     */
    inspection_comment: S.NullOr(S.String),
    metadata: S.NullOr(S.Unknown),
    created_by: S.NullOr(S.UUID),
    created_by_email: S.NullOr(S.String),
    created_by_first_name: S.NullOr(S.String),
    created_by_last_name: S.NullOr(S.String),
    date_created: S.NullOr(S.String),
    date_updated: S.NullOr(S.String),
    /** Advances on every write to the row; an update names the one it read. */
    version: S.Int,
});
export const RiskInstanceNotFound = S.Struct({
    _tag: S.Literal("RiskInstanceNotFound"),
});
/**
 * An update whose `base_version` is not the stored version. The row is
 * unchanged; `current` is what the server holds.
 */
export const RiskInstanceVersionConflict = S.Struct({
    _tag: S.Literal("RiskInstanceVersionConflict"),
    current: RiskInstanceRow,
});
/** A risk is not accepted while one of its draft measures is undecided. */
export const RiskInstanceAcceptBlocked = S.Struct({
    _tag: S.Literal("RiskAcceptBlocked"),
    reason: S.String,
});
const UuidCsvList = S.transform(S.String, S.Array(S.UUID), {
    strict: true,
    decode: (s) => s
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    encode: (ids) => ids.join(","),
});
/**
 * A caller-facing catalog value (`factor`/`subfactor`/`source`/an
 * environment tier) is a free-text label, not an id — split on a bare comma
 * like every other CSV url-param list in this package, trusting values
 * never themselves contain one (true of every risk-catalog value today).
 */
const StringCsvList = S.transform(S.String, S.Array(S.String), {
    strict: true,
    decode: (s) => s
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    encode: (values) => values.join(","),
});
export const ListRiskInstancesParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    language: S.optional(RiskLanguage),
    status: S.optional(RiskInstanceStatus),
    location_id: S.optional(S.UUID),
    /** Assigned-risks-tab's multi-select location filter — combined with `location_id` by AND if both are given. */
    location_ids: S.optional(UuidCsvList),
    is_company_wide: S.optional(S.BooleanFromString),
    search: S.optional(S.String),
    factor: S.optional(StringCsvList),
    subfactor: S.optional(StringCsvList),
    source: S.optional(StringCsvList),
    /**
     * The "environment" picker's 3 tiers (area/object/activity) OR
     * together into one filter, matching the legacy client-side query's
     * `_or` — a row matches if any one of its own area/object/activity
     * value is in the corresponding list.
     */
    environment_area: S.optional(StringCsvList),
    environment_object: S.optional(StringCsvList),
    environment_activity: S.optional(StringCsvList),
}));
export const ListRiskInstances = HttpApiEndpoint.get("listRiskInstances", "/admin/risk-instances")
    .setUrlParams(ListRiskInstancesParams)
    .addSuccess(pagedResponse(RiskInstanceRow))
    .addError(Forbidden, { status: 403 });
export const GetRiskInstanceParams = S.Struct({ id: S.UUID });
export const GetRiskInstance = HttpApiEndpoint.get("getRiskInstance", "/admin/risk-instances/get")
    .setUrlParams(GetRiskInstanceParams)
    .addSuccess(S.Struct({ data: RiskInstanceRow }))
    .addError(RiskInstanceNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * A defect's title. `NonEmptyString` on its own is `minLength(1)`, which a run
 * of spaces satisfies — and a whitespace-only title leaves the same
 * unidentifiable entry in the report and the Risk Center that PMO-202 was
 * about. The value is taken as sent, not trimmed: callers own their whitespace,
 * the endpoint only refuses a title that is no title.
 */
const RiskInstanceTitle = S.NonEmptyString.pipe(S.filter((title) => title.trim().length > 0, {
    identifier: "RiskInstanceTitle",
    description: "a title that is not only whitespace",
}));
export const CreateRiskInstancePayload = S.Struct({
    client_id: S.UUID,
    /**
     * The caller's own stable id for the row it is creating — the offline
     * client's `_temp_id`, written once when the inspector records the risk and
     * never rewritten. Makes this create idempotent: a replay carrying the same
     * value resolves to the row the first attempt already committed instead of
     * inserting a second one (PMO-455).
     *
     * Optional, and unique per client when given. Callers with no client-side
     * identity of their own — the office UI, the risk generator — omit it and
     * are unaffected.
     */
    client_ref: S.optional(S.UUID),
    /** Stable local sibling identity; authority and source version come from domain-data. */
    translate_sibling: S.optional(S.Struct({ targetClientRef: S.UUID })),
    language: RiskLanguage,
    title: RiskInstanceTitle,
    description: S.optional(S.String),
    risk_template_id: S.optional(S.UUID),
    translation_group_id: S.optional(S.Int),
    location_id: S.optional(S.UUID),
    is_company_wide: S.optionalWith(S.Boolean, { default: () => false }),
    severity: S.Int.pipe(S.between(1, 4)),
    probability: S.Int.pipe(S.between(1, 4)),
    status: S.optionalWith(RiskInstanceStatus, { default: () => "draft" }),
    area_translation_group_id: S.Int,
    object_translation_group_id: S.Int,
    activity_translation_group_id: S.Int,
    factor_translation_group_id: S.Int,
    subfactor_translation_group_id: S.Int,
    source_translation_group_id: S.optional(S.Int),
    mode_translation_group_id: S.optional(S.Int),
});
export const CreateRiskInstance = HttpApiEndpoint.post("createRiskInstance", "/admin/risk-instances/create")
    .setPayload(CreateRiskInstancePayload)
    .addSuccess(S.Struct({ data: RiskInstanceRow }))
    .addError(Forbidden, { status: 403 });
const UpdateRiskInstancePayloadFields = S.Struct({
    translate_sibling: S.optional(S.Struct({ targetClientRef: S.UUID })),
    id: S.UUID,
    client_id: S.UUID,
    /** The version the client's edit was based on. */
    base_version: S.optional(S.Int),
    title: S.optional(RiskInstanceTitle),
    description: S.optional(S.NullOr(S.String)),
    location_id: S.optional(S.NullOr(S.UUID)),
    is_company_wide: S.optional(S.Boolean),
    severity: S.optional(S.Int.pipe(S.between(1, 4))),
    probability: S.optional(S.Int.pipe(S.between(1, 4))),
    status: S.optional(RiskInstanceStatus),
    accepted_by: S.optional(S.NullOr(S.UUID)),
    rejected_by: S.optional(S.NullOr(S.UUID)),
    rejection_reason: S.optional(S.NullOr(S.String)),
});
export const UpdateRiskInstancePayload = S.transform(UpdateRiskInstancePayloadFields, S.typeSchema(UpdateRiskInstancePayloadFields), {
    strict: true,
    decode: (payload) => ({
        ...payload,
        base_version: payload.base_version ?? 0,
    }),
    encode: (payload) => payload,
});
export const UpdateRiskInstance = HttpApiEndpoint.post("updateRiskInstance", "/admin/risk-instances/update")
    .setPayload(UpdateRiskInstancePayload)
    .addSuccess(S.Struct({ data: RiskInstanceRow }))
    .addError(RiskInstanceNotFound, { status: 404 })
    .addError(RiskInstanceVersionConflict, { status: 409 })
    .addError(RiskInstanceAcceptBlocked, { status: 400 })
    .addError(Forbidden, { status: 403 });
export const DeleteRiskInstancePayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
});
export const DeleteRiskInstance = HttpApiEndpoint.post("deleteRiskInstance", "/admin/risk-instances/delete")
    .setPayload(DeleteRiskInstancePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(RiskInstanceNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * Retires every language variant of one risk instance group at once — the
 * admin risk-center's bulk delete, replacing the `itemsApi.deleteMany`
 * workaround `riskApi.deleteRisksByTranslationGroup` used.
 */
export const DeleteRiskInstancesByTranslationGroupPayload = S.Struct({
    client_id: S.UUID,
    translation_group_id: S.Int,
});
export const DeleteRiskInstancesByTranslationGroup = HttpApiEndpoint.post("deleteRiskInstancesByTranslationGroup", "/admin/risk-instances/delete-by-translation-group")
    .setPayload(DeleteRiskInstancesByTranslationGroupPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Int }) }))
    .addError(Forbidden, { status: 403 });
/**
 * Bulk read for the inspection screens' offline prefetch: every risk
 * instance of one client whose `translation_group_id` is in the given set,
 * optionally narrowed to one language. POST because the id set can exceed
 * URL limits.
 */
export const ListRiskInstancesByTranslationGroupsPayload = S.Struct({
    client_id: S.UUID,
    translation_group_ids: S.NonEmptyArray(S.Int),
    language: S.optional(RiskLanguage),
});
export const ListRiskInstancesByTranslationGroups = HttpApiEndpoint.post("listRiskInstancesByTranslationGroups", "/admin/risk-instances/list-by-translation-groups")
    .setPayload(ListRiskInstancesByTranslationGroupsPayload)
    .addSuccess(S.Struct({ items: S.Array(RiskInstanceRow) }))
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// Link-table management, mirroring admin-risk-templates' shape.
// ---------------------------------------------------------------------------
export const AddRiskInstanceTagPayload = S.Struct({
    instance_id: S.UUID,
    client_id: S.UUID,
    tag_group_id: S.Int,
});
export const AddRiskInstanceTag = HttpApiEndpoint.post("addRiskInstanceTag", "/admin/risk-instances/tags/add")
    .setPayload(AddRiskInstanceTagPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(RiskInstanceNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveRiskInstanceTagPayload = S.Struct({
    instance_id: S.UUID,
    client_id: S.UUID,
    tag_group_id: S.Int,
});
export const RemoveRiskInstanceTag = HttpApiEndpoint.post("removeRiskInstanceTag", "/admin/risk-instances/tags/remove")
    .setPayload(RemoveRiskInstanceTagPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const AddRiskInstanceLawPayload = S.Struct({
    instance_id: S.UUID,
    client_id: S.UUID,
    law_group_id: S.Int,
});
export const AddRiskInstanceLaw = HttpApiEndpoint.post("addRiskInstanceLaw", "/admin/risk-instances/laws/add")
    .setPayload(AddRiskInstanceLawPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(RiskInstanceNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveRiskInstanceLawPayload = S.Struct({
    instance_id: S.UUID,
    client_id: S.UUID,
    law_group_id: S.Int,
});
export const RemoveRiskInstanceLaw = HttpApiEndpoint.post("removeRiskInstanceLaw", "/admin/risk-instances/laws/remove")
    .setPayload(RemoveRiskInstanceLawPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const AddRiskInstanceMeasureLinkPayload = S.Struct({
    risk_instance_id: S.UUID,
    client_id: S.UUID,
    measure_template_id: S.UUID,
});
export const AddRiskInstanceMeasureLink = HttpApiEndpoint.post("addRiskInstanceMeasureLink", "/admin/risk-instances/measure-links/add")
    .setPayload(AddRiskInstanceMeasureLinkPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.UUID }) }))
    .addError(RiskInstanceNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveRiskInstanceMeasureLinkPayload = S.Struct({
    risk_instance_id: S.UUID,
    client_id: S.UUID,
    measure_template_id: S.UUID,
});
export const RemoveRiskInstanceMeasureLink = HttpApiEndpoint.post("removeRiskInstanceMeasureLink", "/admin/risk-instances/measure-links/remove")
    .setPayload(RemoveRiskInstanceMeasureLinkPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
/**
 * Removes every link between the given risk instances and measure templates
 * in one transaction, and cancels the still-pending draft measures those
 * links generated once no link covers their pair any more (PMO-111). Pairs
 * whose risk instance belongs to another client are ignored. The caller
 * names each language's template id itself; nothing is expanded server-side.
 */
export const RemoveRiskInstanceMeasureLinksPayload = S.Struct({
    client_id: S.UUID,
    risk_instance_ids: S.NonEmptyArray(S.UUID),
    measure_template_ids: S.NonEmptyArray(S.UUID),
});
export const RemoveRiskInstanceMeasureLinks = HttpApiEndpoint.post("removeRiskInstanceMeasureLinks", "/admin/risk-instances/measure-links/remove-many")
    .setPayload(RemoveRiskInstanceMeasureLinksPayload)
    .addSuccess(S.Struct({
    data: S.Struct({
        removed: S.Int,
        cancelled_draft_ids: S.Array(S.UUID),
    }),
}))
    .addError(Forbidden, { status: 403 });
export const RiskInstanceMeasureLinkRow = S.Struct({
    id: S.UUID,
    risk_instance_id: S.UUID,
    measure_template_id: S.UUID,
    date_created: S.NullOr(S.String),
});
/**
 * `risk_instance_measure_links` carries no `client_id` of its own — this
 * intent scopes by joining to `risk_instances` and only ever returns links
 * for instances that are both given and owned by `client_id`, the same
 * ownership check `addRiskInstanceMeasureLink` re-verifies on write.
 */
export const ListRiskInstanceMeasureLinksPayload = S.Struct({
    client_id: S.UUID,
    risk_instance_ids: S.NonEmptyArray(S.UUID),
});
export const ListRiskInstanceMeasureLinks = HttpApiEndpoint.post("listRiskInstanceMeasureLinks", "/admin/risk-instances/measure-links/list")
    .setPayload(ListRiskInstanceMeasureLinksPayload)
    .addSuccess(S.Struct({ items: S.Array(RiskInstanceMeasureLinkRow) }))
    .addError(Forbidden, { status: 403 });
export const AddRiskInstanceAttachmentPayload = S.Struct({
    instance_id: S.UUID,
    client_id: S.UUID,
    file_id: S.UUID,
});
export const AddRiskInstanceAttachment = HttpApiEndpoint.post("addRiskInstanceAttachment", "/admin/risk-instances/attachments/add")
    .setPayload(AddRiskInstanceAttachmentPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(RiskInstanceNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveRiskInstanceAttachmentPayload = S.Struct({
    instance_id: S.UUID,
    client_id: S.UUID,
    file_id: S.UUID,
});
export const RemoveRiskInstanceAttachment = HttpApiEndpoint.post("removeRiskInstanceAttachment", "/admin/risk-instances/attachments/remove")
    .setPayload(RemoveRiskInstanceAttachmentPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const adminRiskInstancesGroup = HttpApiGroup.make("admin-risk-instances")
    .add(ListRiskInstances)
    .add(GetRiskInstance)
    .add(CreateRiskInstance)
    .add(UpdateRiskInstance)
    .add(DeleteRiskInstance)
    .add(DeleteRiskInstancesByTranslationGroup)
    .add(ListRiskInstancesByTranslationGroups)
    .add(AddRiskInstanceTag)
    .add(RemoveRiskInstanceTag)
    .add(AddRiskInstanceLaw)
    .add(RemoveRiskInstanceLaw)
    .add(AddRiskInstanceMeasureLink)
    .add(RemoveRiskInstanceMeasureLink)
    .add(RemoveRiskInstanceMeasureLinks)
    .add(ListRiskInstanceMeasureLinks)
    .add(AddRiskInstanceAttachment)
    .add(RemoveRiskInstanceAttachment)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-risk-instances.js.map